import { useState, type FormEvent } from 'react'
import { Button } from '@floorfile/ui'
import { useCopy } from '@floorfile/i18n'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { appCopy } from '../copy'

/**
 * Invitation acceptance — the page the "You have been invited to FloorFile"
 * email links to (`/accept-invite?token=…`). Collects the invitee's name and
 * password, POSTs to `/auth/accept-invite` (the token is the credential) and
 * lands signed in as an ACTIVE, email-verified user.
 *
 * Audit FF-AUDIT-058: the API endpoint and the email link both existed, the
 * page did not, so every invitation ended on the not-found card. The e2e
 * suite never noticed because it read tokens from the wrangler log instead of
 * following the link; `auth-journey.spec.ts` now follows the emailed URL.
 */

const field = 'w-full rounded-md border bg-background px-3 py-2'

/** The API error code, if the thrown value carries one (`ApiError` from the
 * SDK). Read structurally rather than by `instanceof`: under Vite's dev
 * pre-bundling the SDK can be instantiated twice and the class check fails
 * while the shape is exactly right. */
function errorCode(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null || !('code' in err)) return undefined
  const code = (err as { code: unknown }).code
  return typeof code === 'string' ? code : undefined
}

function Shell({ children }: { children: React.ReactNode }) {
  const copy = useCopy(appCopy)
  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6">
        <h1 className="text-xl font-semibold">{copy.shell.brand}</h1>
        {children}
      </div>
    </main>
  )
}

export function AcceptInvitePage() {
  const copy = useCopy(appCopy)
  const c = copy.acceptInvite
  const { acceptInvite } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(undefined)
    if (password !== confirm) {
      setError(copy.registration.mismatch)
      return
    }
    if (password.length < 12) {
      setError(copy.registration.tooShort)
      return
    }
    setBusy(true)
    try {
      await acceptInvite({ token, firstName, lastName, password })
      navigate('/', { replace: true })
    } catch (err) {
      const code = errorCode(err)
      if (code === 'NOT_FOUND') setError(c.invalid)
      else if (code === 'UNAUTHENTICATED') setError(c.noLongerValid)
      else if (code === 'VALIDATION_FAILED' && err instanceof Error && err.message) setError(err.message)
      else setError(err instanceof Error && err.message ? err.message : c.failed)
    } finally {
      setBusy(false)
    }
  }

  if (!token) {
    return (
      <Shell>
        <p className="text-sm text-destructive" role="alert">{c.missingToken}</p>
        <Link to="/login" className="text-sm text-primary hover:underline">
          {copy.authCommon.goToSignIn}
        </Link>
      </Shell>
    )
  }

  return (
    <Shell>
      <div>
        <h2 className="text-lg font-semibold">{c.title}</h2>
        <p className="text-sm text-muted-foreground">{c.prompt}</p>
      </div>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">{copy.registration.firstName}</span>
            <input required maxLength={100} value={firstName} onChange={(e) => setFirstName(e.target.value)} className={field} autoComplete="given-name" />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">{copy.registration.lastName}</span>
            <input required maxLength={100} value={lastName} onChange={(e) => setLastName(e.target.value)} className={field} autoComplete="family-name" />
          </label>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">{copy.authCommon.password}</span>
          <input type="password" required minLength={12} maxLength={200} value={password} onChange={(e) => setPassword(e.target.value)} className={field} autoComplete="new-password" />
          <span className="mt-1 block text-xs text-muted-foreground">{copy.registration.passwordHint}</span>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">{copy.registration.confirmPassword}</span>
          <input type="password" required minLength={12} maxLength={200} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field} autoComplete="new-password" />
        </label>

        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

        <Button className="w-full" type="submit" disabled={busy}>
          {busy ? c.accepting : c.accept}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          {copy.registration.existingAccount}{' '}
          <Link to="/login" className="text-primary underline">
            {copy.registration.signIn}
          </Link>
        </p>
      </form>
    </Shell>
  )
}
